import { computed, ref } from 'vue'

import { identityDataForStripe } from '@/helpers/identityForm/identityDataForStripe'
import { getIdentityPayload } from '@/helpers/identityForm/identityHelpers'
import { isPlaceholderEmail } from '@/helpers/placeholderEmail'

import { upsertUserAndGetIdentityId } from '@/services/user'

import { useIdentityStore } from '@/stores/identity'

import type IdentityForm from '@/components/IdentityForm.vue'
import type MemberEmailForm from '@/components/MemberEmailForm.vue'
import type { IdentityCheckoutData, ValidatedIdentityResult } from '@/components/donations/checkout.types'

/**
 * Form refs plus pay-time identity bindings. PaymentComponent invokes these at Pay; it does not own upsert / PATCH.
 */
export function useCheckoutIdentity() {
  const identityStore = useIdentityStore()
  const identityFormRef = ref<InstanceType<typeof IdentityForm> | null>(null)
  const memberEmailFormRef = ref<InstanceType<typeof MemberEmailForm> | null>(null)

  const showIdentityForm = computed(() => !identityStore.isLoggedIn)
  const requireEmailForMember = computed(() => identityStore.isLoggedIn && isPlaceholderEmail(identityStore.email))

  const guestFormData = computed(() => identityFormRef.value?.finalFormData ?? {})
  const memberEmail = computed(() => memberEmailFormRef.value?.finalFormData.email)

  /** Validate the identity form and return the validated identity data without side effects. */
  function getValidatedIdentity(): ValidatedIdentityResult {
    const { error: validationError } = identityFormRef.value?.validate() ?? memberEmailFormRef.value?.validate() ?? {}
    if (validationError) return { status: 'error', error: validationError }

    if (identityStore.isLoggedIn) {
      return {
        status: 'success',
        data: identityDataForStripe(
          {},
          {
            name: identityStore.name,
            email: memberEmail.value ?? identityStore.email,
            meta: identityStore.identityMeta ?? {},
          },
        ),
      }
    }

    return { status: 'success', data: identityDataForStripe(guestFormData.value) }
  }

  /** Resolve the identity data after guest upsert / member PATCH. */
  async function resolveIdentityData(): Promise<IdentityCheckoutData> {
    if (identityStore.isLoggedIn) {
      const newEmail = memberEmail.value
      if (newEmail) {
        const patch = await identityStore.updateEmail(newEmail)
        if (patch.status === 'error') return patch
        return { status: 'success', receiptEmail: newEmail }
      }

      const existingEmail = identityStore.email
      if (!existingEmail || isPlaceholderEmail(existingEmail)) {
        return { status: 'error', error: new Error('Please enter a valid email address.') }
      }

      return { status: 'success', receiptEmail: existingEmail }
    }

    const guestData = guestFormData.value
    const upsert = await upsertUserAndGetIdentityId(getIdentityPayload(guestData))
    if (upsert.status === 'error') return upsert

    return {
      status: 'success',
      receiptEmail: guestData.email as string,
      ...(upsert.identityId ? { guest_identity_id: upsert.identityId } : {}),
    }
  }

  return {
    identityFormRef,
    memberEmailFormRef,
    showIdentityForm,
    requireEmailForMember,
    getValidatedIdentity,
    resolveIdentityData,
  }
}
